/**
 * Which support coordinates a screen is allowed to publish, and to whom (DEV-7932).
 *
 * Five places used to print `1-800-361-6068 (Option 3)` on their own — the footer, the
 * maintenance page, the WAF/file error sentences and the two `errorTransformer` messages —
 * each with its own copy of the number, and three of them with it a second time as an i18n
 * inline default (`t('footer.phone', '1-800-361-6068')`). Five copies, one rule: whether the
 * coordinates exist at all, and what they are, is decided HERE and nowhere else.
 *
 * ## The rule
 *
 * | `features.displaySupportContact` | `resolveSupportContact` |
 * |----------------------------------|-------------------------|
 * | `true`                           | the coordinates         |
 * | `false`                          | `null`                  |
 * | absent (older Edge, warm cache)  | `null`                  |
 * | no tenant config at all          | `null`                  |
 *
 * **`null` means "render nothing at all" — the label, the phone, the option, the `tel:` link
 * and the surrounding sentence all disappear.** It never means "fall back to a default
 * number". There is deliberately no second source to try: the flag says WHOSE portal this is,
 * so an absent answer is not a gap to fill, it is the statement that no line may be shown.
 * A call site that reads `?? DEFAULT_PHONE` reintroduces exactly the defect this story closes.
 *
 * ## Why the flag exists
 *
 * The BFF projects `displaySupportContact` from `publicOpen.auth0OrgId` as the inverse of
 * "served through the generic portal shell" (see `TenantService.buildFeaturesConfig`). That
 * shell is shared by several corporate clients, while `1-800-361-6068` reaches ONE insurer's
 * travel-claims department. Printed on the shell it routes another insurer's members to a
 * service that cannot open their file — and the claimant has no way to tell that the number
 * they were given was never theirs. The tenant embedded in its own Espace client speaks for
 * itself, keeps its line, and is the `true` case.
 *
 * ## Strict `=== true`, so the whole cascade fails CLOSED
 *
 * The field is additive: an Edge that has not deployed this story yet omits it, and so does a
 * `TenantConfig` cached in localStorage before it shipped. Both read as `null` here, i.e. the
 * line is hidden until a config that explicitly authorizes it arrives. That is the recoverable
 * error of the two — a hidden number is a support call, a wrong claims line is a member sent to
 * the wrong insurer. `services/tenantService.getDefaultBrandingConfig` defaults the same way,
 * and `CACHE_SCHEMA_VERSION` was bumped `v4` → `v5` in the same service so the warm caches are
 * a MISS instead of 24 h of silently hidden coordinates on the tenant that keeps its footer.
 *
 * ## Data, not a rendered sentence
 *
 * The resolver returns the coordinates as fields because the five call sites frame them five
 * ways: the footer sets the number in its own `<span>`, the maintenance page needs a dialable
 * `href`, and the error transformer needs them inside a full sentence in the claimant's
 * language. Returning pre-rendered text would force one of those to re-parse it.
 *
 * ## Why the literals live here rather than in i18n
 *
 * `footer.phone` and `footer.option` are byte-identical in `fr.json` and `en.json`: a phone
 * number is not prose, it does not translate, and a missing translation key is precisely how
 * the number survived in the inline defaults. Keeping them as module constants means the
 * coordinates cannot come back through a locale file, and there is one place to look when
 * they change.
 *
 * ## Single reader — DEV-7934 replaces the body, not the call sites
 *
 * `displaySupportContact` is a boolean only because phase 1 has one set of coordinates.
 * DEV-7934 serves per-corporate-client contact data, and its whole job is to change the body
 * of `resolveSupportContact` — pick the caller's client, return its number — while the five
 * display sites keep consuming `SupportContact | null` unchanged. That only holds while this
 * module is the ONLY reader of the flag: a second `features.displaySupportContact` anywhere
 * in `src/` turns that ticket back into a five-file change.
 */

import type { TenantConfig } from '../types/tenant';

/** Travel-claims assistance line of the tenant authorized to publish it. */
const SUPPORT_PHONE = '1-800-361-6068';

/** Menu entry that reaches travel claims once the line answers. */
const SUPPORT_PHONE_OPTION = '(Option 3)';

/**
 * `tel:` target in RFC 3966 global form. Derived from the digits of `SUPPORT_PHONE` — which
 * already start with the North American country code — so the two can never drift apart, and
 * so no call site has to know how to turn a human-readable number into a dialable one.
 */
const SUPPORT_TEL_HREF = `tel:+${SUPPORT_PHONE.replace(/\D/g, '')}`;

/**
 * Support coordinates, as data. Every field is public business information: nothing here is
 * PII, and nothing here is claimant-specific.
 */
export interface SupportContact {
  /** Number as the claimant reads and dictates it, e.g. `1-800-361-6068`. */
  phone: string;
  /** Menu entry to select, e.g. `(Option 3)`. Rendered next to `phone`, never merged into it. */
  option: string;
  /** Ready-to-use `href` for a click-to-call link, e.g. `tel:+18003616068`. */
  telHref: string;
}

/**
 * The coordinates this tenant may publish, or `null` when none may be shown.
 *
 * `null` is an instruction to render NOTHING (see the module header) — never a cue to
 * substitute a default number. Accepts a possibly-absent config because consumers read it from
 * `useEmbeddedApp()`, where it is `null` until the `CONTEXT_RESPONSE` handshake resolves and
 * stays `null` in standalone mode.
 */
export const resolveSupportContact = (
  tenantConfig: TenantConfig | null | undefined
): SupportContact | null =>
  tenantConfig?.features?.displaySupportContact === true
    ? { phone: SUPPORT_PHONE, option: SUPPORT_PHONE_OPTION, telHref: SUPPORT_TEL_HREF }
    : null;
